import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Diamond } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { cartCount, setIsCartOpen } = useCart();
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';
  const headerBg = scrolled || !isHome
    ? 'bg-[#0a0a0a]/95 backdrop-blur-md shadow-lg'
    : 'bg-transparent';

  const navLinks = [
    { to: '/shop?category=watch-accessories', label: 'Watch Accessories' },
    { to: '/shop?category=watch-straps', label: 'Watch Straps' },
    { to: '/shop?category=phone-cases', label: 'Phone Cases' },
    { to: '/shop?category=jewelry-accessories', label: 'Jewelry Accessories' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <Diamond className="w-7 h-7 text-[#2EC866] transition-transform duration-300 group-hover:rotate-12" />
            <div className="flex flex-col">
              <span className="text-white font-bold text-xl tracking-wider" style={{ fontFamily: 'Playfair Display, serif' }}>
                LUXO-MARKET
              </span>
              <span className="text-[#2EC866] text-[9px] tracking-[0.3em] uppercase font-medium -mt-1">
                Luxury Accessories
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, i) => (
              <React.Fragment key={link.to}>
                <Link
                  to={link.to}
                  className="text-gray-300 hover:text-[#2EC866] text-sm font-medium px-4 py-2 transition-colors duration-300"
                >
                  {link.label}
                </Link>
                {i < navLinks.length - 1 && (
                  <span className="text-gray-600 text-xs">|</span>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* Right Icons */}
          <div className="flex items-center gap-4">
            <Link
              to={isAuthenticated ? '/account' : '/account'}
              className="text-gray-300 hover:text-[#2EC866] transition-colors duration-300"
            >
              <User className="w-5 h-5" />
            </Link>
            <Link
              to="/cart"
              className="relative text-gray-300 hover:text-[#2EC866] transition-colors duration-300"
            >
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#2EC866] text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden text-gray-300 hover:text-white transition-colors"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
        menuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="bg-[#0a0a0a]/98 backdrop-blur-md border-t border-gray-800 px-4 py-4">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className="block py-3 text-gray-300 hover:text-[#2EC866] text-sm font-medium border-b border-gray-800/50 transition-colors"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
};

export default Header;
